import { DataTable, DataTableFilterMeta } from 'primereact/datatable';
import { Column, ColumnFilterElementTemplateOptions } from 'primereact/column';
import React, { useState, useMemo, useCallback } from 'react';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Dropdown } from 'primereact/dropdown';
import { Tooltip } from 'primereact/tooltip';
import { FilterMatchMode } from 'primereact/api';
import dateFormat from '../../../../../../utils/date-format';
import dateUtils from '../../../../../../utils/date-format';
import { UserPaymentServiceWithPriceList } from '../../../../../../api/types/students/payment-services/user-payments/user-payment-service-with-price-list';
import { UserSummaryType } from '../../../../../../api/types/user/user-summary.type';
import '../../../../../../component/styles/table-view-filter-width.css';
import { PaymentServiceHistoryList } from '../../../payment-services/componenets/payment-service-history-list/payment-service-history-list';
import { AddPaymentServiceDialog } from '../../../payment-services/componenets/add-payment-service-dialog';
import { WarningDialog } from '../../../../../../component/ui/warning-dialog/warning-dialog';
import { deletePaymentServiceAllocationOperation } from '../../payment-services/operations/delete-payment-service-allocation-operation';
import { AddCreditCardTokenDialog } from './components/add-credit-card-token-dialog';

type PaymentStatus = 'שולם' | 'שולם חלקית' | 'לא שולם';

export type EnrichedUserPaymentService = UserPaymentServiceWithPriceList & {
  totalPaymentAmount: number;
  paymentStatus: PaymentStatus;
  installmentAmount: number;
};

interface UserPagePaymentsTableProps {
  user: UserSummaryType;
  enrichedPaymentServices: EnrichedUserPaymentService[];
  creditCardInstallmentPlans: any[];
  bankTransferInstallmentPlans: any[];
  selectedRows: EnrichedUserPaymentService[];
  onSelectionChange: (rows: EnrichedUserPaymentService[]) => void;
  onPayment: (
    allocationId: number,
    paymentServiceName: string,
    amount: number,
  ) => void;
  onPaySelectedRows: () => void;
}

export const UserPagePaymentsTable: React.FC<UserPagePaymentsTableProps> =
  React.memo(
    ({
      user,
      enrichedPaymentServices,
      creditCardInstallmentPlans,
      bankTransferInstallmentPlans,
      selectedRows,
      onSelectionChange,
      onPayment,
      onPaySelectedRows,
    }) => {
      const [showAddPaymentServiceDialog, setShowAddPaymentServiceDialog] =
        useState(false);
      const [showAddCreditCardTokenDialog, setShowAddCreditCardTokenDialog] =
        useState(false);
      const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>(
        {},
      );
      const [showDeleteAllocationDialog, setShowDeleteAllocationDialog] =
        useState(false);
      const [allocationIdToDelete, setAllocationIdToDelete] = useState<
        number | null
      >(null);
      const [deleteLoading, setDeleteLoading] = useState(false);
      const [filters, setFilters] = useState<DataTableFilterMeta>({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
        'paymentService.paymentServiceName': {
          value: null,
          matchMode: FilterMatchMode.CONTAINS,
        },
        'currentPrices.price': {
          value: null,
          matchMode: FilterMatchMode.CONTAINS,
        },
        totalPaymentAmount: {
          value: null,
          matchMode: FilterMatchMode.CONTAINS,
        },
        'allocations.allocationEntity.createdAt': {
          value: null,
          matchMode: FilterMatchMode.CONTAINS,
        },
        'allocations.programEntity.programName': {
          value: null,
          matchMode: FilterMatchMode.CONTAINS,
        },
        'allocations.branch.branchName': {
          value: null,
          matchMode: FilterMatchMode.CONTAINS,
        },
        'allocations.userPayments[0].paymentDate': {
          value: null,
          matchMode: FilterMatchMode.CONTAINS,
        },
        paymentStatus: {
          value: null,
          matchMode: FilterMatchMode.EQUALS,
        },
      });

      const payedStatus: PaymentStatus = 'שולם';
      const partialStatus: PaymentStatus = 'שולם חלקית';
      const missingPaymentStatus: PaymentStatus = 'לא שולם';
      const [statuses] = useState([
        payedStatus,
        partialStatus,
        missingPaymentStatus,
      ]);

      const getEffectivePrice = useCallback(
        (
          service: UserPaymentServiceWithPriceList | EnrichedUserPaymentService,
        ): number => {
          const allocation = service.allocations?.allocationEntity;
          const override =
            (allocation?.overrideSum as number | null | undefined) ??
            (allocation?.overridePrice as number | null | undefined) ??
            null;
          const basePrice = service.currentPrices?.price ?? 0;
          return override !== null && !Number.isNaN(override)
            ? override
            : basePrice;
        },
        [],
      );

      const statusItemTemplate = useCallback((option: PaymentStatus) => {
        return option === 'שולם' ? (
          <Tag style={{ width: '70px' }} severity="success" value="שולם"></Tag>
        ) : option === 'שולם חלקית' ? (
          <Tag
            style={{ width: '70px' }}
            severity="warning"
            value="שולם חלקית"
          ></Tag>
        ) : (
          <Tag
            style={{ width: '70px' }}
            severity="danger"
            value="לא שולם"
          ></Tag>
        );
      }, []);

      const statusFilterElement = useCallback(
        (options: ColumnFilterElementTemplateOptions) => {
          return (
            <Dropdown
              value={options.value}
              options={statuses}
              onChange={(e) => options.filterApplyCallback(e.value)}
              itemTemplate={statusItemTemplate}
              className="p-column-filter"
              style={{
                width: '130px',
                height: '30px',
                alignItems: 'center',
              }}
            />
          );
        },
        [statuses, statusItemTemplate],
      );

      const handleRowClick = useCallback((data: any) => {
        if (data.userPayments?.length > 0) {
          const id = data.allocations.allocationEntity.id;
          setExpandedRows((prev) => {
            const newState = { ...prev };
            if (newState[id]) {
              delete newState[id];
            } else {
              newState[id] = true;
            }
            return newState;
          });
        }
      }, []);

      const handleRowToggle = useCallback((e: any) => {
        setExpandedRows(e.data);
      }, []);

      const handleSelectionChange = useCallback(
        (e: any) => {
          onSelectionChange(e.value as EnrichedUserPaymentService[]);
        },
        [onSelectionChange],
      );

      const closeDeleteDialog = useCallback(() => {
        if (deleteLoading) return;
        setShowDeleteAllocationDialog(false);
        setAllocationIdToDelete(null);
      }, [deleteLoading]);

      const confirmDeleteAllocation = useCallback(async () => {
        if (allocationIdToDelete === null) return;
        await deletePaymentServiceAllocationOperation(
          allocationIdToDelete,
          user.id,
          setDeleteLoading,
          closeDeleteDialog,
        );
      }, [allocationIdToDelete, user.id, closeDeleteDialog]);

      // Memoize row expansion template
      const rowExpansionTemplate = useCallback(
        (data: EnrichedUserPaymentService) => (
          <PaymentServiceHistoryList
            payments={data.userPayments}
            totalPrice={data.currentPrices.price}
            user={user}
            allocationId={data.allocations.allocationEntity.id}
            creditCardInstallmentPlans={creditCardInstallmentPlans}
            bankTransferInstallmentPlans={bankTransferInstallmentPlans}
          />
        ),
        [user, creditCardInstallmentPlans, bankTransferInstallmentPlans],
      );

      // Memoize table style
      const tableStyle = useMemo(
        () => ({
          direction: 'rtl' as const,
          marginTop: '16px',
        }),
        [],
      );

      return (
        <>
          <div
            style={{
              marginTop: '16px',
              marginBottom: '8px',
              display: 'flex',
              gap: '12px',
              alignItems: 'center',
            }}
          >
            <Button
              label="הוסף שרות תשלום"
              icon="pi pi-plus"
              onClick={() => setShowAddPaymentServiceDialog(true)}
              style={{
                backgroundColor: '#2196F3',
                border: 'none',
                color: 'white',
              }}
            />
            {/*            <Button
              label="הוספת כרטיס אשראי"
              icon="pi pi-credit-card"
              onClick={() => setShowAddCreditCardTokenDialog(true)}
              style={{
                backgroundColor: '#2196F3',
                border: 'none',
                color: 'white',
              }}
            />*/}
            {selectedRows.length > 0 && (
              <Button
                label={`שלם עבור ${selectedRows.length} שרותים נבחרים`}
                icon="pi pi-credit-card"
                onClick={onPaySelectedRows}
                style={{
                  backgroundColor: '#00C853',
                  border: 'none',
                  color: 'white',
                }}
              />
            )}
          </div>

          <AddPaymentServiceDialog
            visible={showAddPaymentServiceDialog}
            onHide={() => setShowAddPaymentServiceDialog(false)}
            user={user}
          />

          <AddCreditCardTokenDialog
            visible={showAddCreditCardTokenDialog}
            onHide={() => setShowAddCreditCardTokenDialog(false)}
            userId={user.id}
          />

          <WarningDialog
            header="מחיקת שירות תשלום מהתלמיד"
            isVisible={showDeleteAllocationDialog}
            onHide={closeDeleteDialog}
            addIcon
            content="אתה עומד למחוק הקצאת שירות תשלום מהתלמיד."
            notInformation="שימו לב: ניתן למחוק רק שירותי תשלום שאינם משויכים לתוכנית לימודים ושלא בוצע בהם אף תשלום."
            confirmLabel="מחק"
            cancelLabel="ביטול"
            onConfirm={confirmDeleteAllocation}
            confirmLoading={deleteLoading}
          />

          <DataTable
            value={enrichedPaymentServices}
            expandedRows={expandedRows}
            onRowToggle={handleRowToggle}
            rowExpansionTemplate={rowExpansionTemplate}
            onRowClick={(e) => handleRowClick(e.data)}
            dataKey="allocations.allocationEntity.id"
            emptyMessage={
              <div style={{ textAlign: 'right' }}>לא נמצאו נתונים</div>
            }
            selectionMode="checkbox"
            selection={selectedRows}
            onSelectionChange={handleSelectionChange}
            filterDisplay={'row'}
            groupRowsBy="dsf"
            filters={filters}
            onFilter={(e) => setFilters(e.filters)}
            scrollable
            tableStyle={tableStyle}
          >
            <Column
              selectionMode="multiple"
              headerStyle={{ width: '3rem' }}
              style={{ width: '3rem', textAlign: 'center' }}
              frozen={true}
            />
            <Column
              expander={true}
              style={{ width: '3rem', textAlign: 'center' }}
              frozen={true}
              body={useCallback(
                (data: EnrichedUserPaymentService) => {
                  const hasPayments = data.userPayments?.length > 0;
                  const isExpanded =
                    expandedRows[data.allocations.allocationEntity.id];

                  return (
                    <div
                      className={`expander-icon ${hasPayments ? 'has-payments' : ''}`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '0.5rem',
                      }}
                    >
                      <i
                        className={`pi ${isExpanded ? 'pi-chevron-up' : 'pi-chevron-down'}`}
                        style={{
                          color: hasPayments ? '#2196F3' : '#ccc',
                          fontSize: '1rem',
                          transform: `translateY(${isExpanded ? '-1px' : '1px'})`,
                          transition: 'transform 0.15s ease-in-out',
                        }}
                      />
                    </div>
                  );
                },
                [expandedRows],
              )}
            />
            <Column
              style={{
                textAlign: 'right',
                height: '40px',
                minWidth: '150px',
              }}
              field="paymentService.paymentServiceName"
              header="שם שרות"
              sortable={true}
              headerStyle={{
                height: '45px',
                maxHeight: '45px',
                padding: '0',
                paddingRight: '16.5px',
              }}
              filter={true}
              filterField="paymentService.paymentServiceName"
              filterPlaceholder="שם שרות"
              filterMatchMode="contains"
              showFilterMenu={false}
            />

            <Column
              style={{ textAlign: 'right', height: '40px', maxWidth: '80px' }}
              field="currentPrices.price"
              header="סכום"
              sortable={true}
              headerStyle={{
                height: '45px',
                maxHeight: '45px',
                padding: '0',
                paddingRight: '16.5px',
                minWidth: '150px',
              }}
              body={useCallback(
                (rowData: EnrichedUserPaymentService) => (
                  <span style={{ direction: 'ltr', display: 'inline-block' }}>
                    ₪{rowData.currentPrices.price.toLocaleString('en-US')}
                  </span>
                ),
                [],
              )}
              filter={true}
              filterField="currentPrices.price"
              filterPlaceholder="שם שרות"
              filterMatchMode="contains"
              showFilterMenu={false}
            />

            <Column
              style={{ textAlign: 'right', height: '40px', maxWidth: '80px' }}
              header="מחיר מיוחד"
              sortable={false}
              headerStyle={{
                height: '45px',
                maxHeight: '45px',
                padding: '0',
                paddingRight: '16.5px',
                minWidth: '150px',
              }}
              body={useCallback((rowData: EnrichedUserPaymentService) => {
                const allocation = rowData.allocations.allocationEntity;
                const override =
                  (allocation.overrideSum as number | null | undefined) ??
                  (allocation.overridePrice as number | null | undefined) ??
                  null;
                if (
                  override === null ||
                  override === undefined ||
                  Number.isNaN(override)
                ) {
                  return (
                    <span style={{ direction: 'ltr', display: 'inline-block' }}>
                      -
                    </span>
                  );
                }
                return (
                  <span
                    style={{
                      direction: 'ltr',
                      display: 'inline-block',
                      fontWeight: 'bold',
                    }}
                  >
                    ₪{override}
                  </span>
                );
              }, [])}
            />

            <Column
              style={{ textAlign: 'right', height: '40px', maxWidth: '80px' }}
              header="שולם"
              sortable={true}
              headerStyle={{
                height: '45px',
                maxHeight: '45px',
                padding: '0',
                paddingRight: '16.5px',
                minWidth: '150px',
              }}
              field={'totalPaymentAmount'}
              body={useCallback(
                (rowData: EnrichedUserPaymentService) => (
                  <span style={{ direction: 'ltr', display: 'inline-block' }}>
                    ₪
                    {rowData.totalPaymentAmount.toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                ),
                [],
              )}
              filter={true}
              filterField="totalPaymentAmount"
              filterPlaceholder="סכום"
              showFilterMenu={false}
            />

            <Column
              style={{ textAlign: 'right', height: '40px', maxWidth: '120px' }}
              header={
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '4px',
                    alignItems: 'center',
                  }}
                >
                  הסדר תשלום
                  <Tooltip target=".installment-info-icon" position="top" />
                  <i
                    className="pi pi-info-circle installment-info-icon"
                    data-pr-tooltip="כלל התשלומים המתוכננים לשלם בהוראות הקבע"
                    style={{
                      cursor: 'pointer',
                      color: '#6366F1',
                      fontSize: '1rem',
                    }}
                  />
                </div>
              }
              sortable={true}
              headerStyle={{
                height: '45px',
                maxHeight: '45px',
                padding: '0',
                paddingRight: '16.5px',
                minWidth: '120px',
              }}
              field={'installmentAmount'}
              body={useCallback(
                (rowData: EnrichedUserPaymentService) => (
                  <div
                    style={{
                      color:
                        rowData.installmentAmount > 0 ? '#1976D2' : '#9CA3AF',
                      fontWeight:
                        rowData.installmentAmount > 0 ? 'bold' : 'normal',
                    }}
                  >
                    {rowData.installmentAmount > 0
                      ? `₪${rowData.installmentAmount.toLocaleString()}`
                      : '-'}
                  </div>
                ),
                [],
              )}
            />

            <Column
              style={{ textAlign: 'right', height: '40px', maxWidth: '100px' }}
              header="תאריך יצירה"
              field={'allocations.allocationEntity.createdAt'}
              sortable={true}
              headerStyle={{
                height: '45px',
                maxHeight: '45px',
                padding: '0',
                paddingRight: '16.5px',
              }}
              body={useCallback(
                (rowData: UserPaymentServiceWithPriceList) => (
                  <div>
                    {dateFormat.displayFormatDate(
                      rowData.allocations.allocationEntity.createdAt,
                    )}
                  </div>
                ),
                [],
              )}
              filterElement={useCallback(
                (options: ColumnFilterElementTemplateOptions) => (
                  <input
                    type="text"
                    value={options.value || ''}
                    onChange={(e) =>
                      options.filterApplyCallback(e.target.value)
                    }
                    style={{
                      width: '80px',
                      paddingTop: '5px',
                      paddingBottom: '5px',
                    }}
                    className="p-inputtext p-component"
                  />
                ),
                [],
              )}
              filterFunction={useCallback((value: any, filter: string) => {
                if (!filter || filter.trim() === '') {
                  return true;
                }

                const dateStr = dateFormat.displayFormatDate(value);
                return dateStr.toLowerCase().includes(filter.toLowerCase());
              }, [])}
              filter={true}
              filterField="allocations.allocationEntity.createdAt"
              filterMatchMode="contains"
              showFilterMenu={false}
              showClearButton={false}
            />

            <Column
              style={{ textAlign: 'right', height: '40px' }}
              header="תוכנית לימודים"
              field={'allocations.programEntity.programName'}
              body={useCallback((rowData: UserPaymentServiceWithPriceList) => {
                return (
                  <div>
                    {rowData.allocations.programEntity?.programName}
                    {rowData.allocations.programEntity?.programName &&
                      rowData.allocations.programEntity
                        ?.additionalProgramName &&
                      ` - ${rowData.allocations.programEntity?.additionalProgramName}`}
                  </div>
                );
              }, [])}
              sortable={true}
              headerStyle={{
                height: '45px',
                maxHeight: '45px',
                padding: '0',
                paddingRight: '16.5px',
              }}
              filterElement={useCallback(
                (options: ColumnFilterElementTemplateOptions) => (
                  <input
                    type="text"
                    value={options.value || ''}
                    onChange={(e) => {
                      options.filterApplyCallback(e.target.value);
                    }}
                    style={{
                      width: '80px',
                      height: '30px',
                      padding: '5px',
                    }}
                    className="p-inputtext p-component"
                    placeholder="מוען"
                  />
                ),
                [],
              )}
              filter={true}
              filterField="allocations.programEntity.programName"
              filterMatchMode="contains"
              showFilterMenu={false}
            />

            <Column
              style={{ textAlign: 'right', height: '40px' }}
              header="שלוחה"
              field={'allocations.branch.branchName'}
              sortable={true}
              headerStyle={{
                height: '45px',
                maxHeight: '45px',
                padding: '0',
                paddingRight: '16.5px',
              }}
              filterElement={useCallback(
                (options: ColumnFilterElementTemplateOptions) => (
                  <input
                    type="text"
                    value={options.value || ''}
                    onChange={(e) => {
                      options.filterApplyCallback(e.target.value);
                    }}
                    style={{
                      width: '80px',
                      height: '30px',
                      padding: '5px',
                    }}
                    className="p-inputtext p-component"
                    placeholder="שלוחה"
                  />
                ),
                [],
              )}
              filter={true}
              filterField="allocations.branch.branchName"
              filterMatchMode="contains"
              showFilterMenu={false}
            />

            <Column
              style={{ textAlign: 'center', height: '40px', maxWidth: '100px' }}
              header="תאריך תשלום"
              field="allocations.userPayments[0]"
              sortable={true}
              headerStyle={{
                height: '45px',
                maxHeight: '45px',
                padding: '0',
              }}
              filter={true}
              filterField="allocations.userPayments[0].paymentDate"
              filterMatchMode="contains"
              showFilterMenu={false}
              filterElement={useCallback(
                (options: ColumnFilterElementTemplateOptions) => (
                  <input
                    type="text"
                    value={options.value || ''}
                    onChange={(e) => {
                      const newValue = e.target.value || '';
                      options.filterApplyCallback(newValue);
                    }}
                    style={{
                      width: '80px',
                      paddingTop: '5px',
                      paddingBottom: '5px',
                    }}
                    className="p-inputtext p-component"
                  />
                ),
                [],
              )}
              filterFunction={useCallback((value: any, filter: string) => {
                if (!filter || filter.trim() === '') {
                  return true;
                }

                const dateStr = dateFormat.displayFormatDate(value);
                return dateStr.toLowerCase().includes(filter.toLowerCase());
              }, [])}
              body={useCallback((rowData: UserPaymentServiceWithPriceList) => {
                let date: Date | null = null;
                if (rowData.allocations.userPayments?.length) {
                  date = new Date(
                    rowData.allocations.userPayments[0].paymentDate,
                  );
                }
                return (
                  <div style={{ textAlign: 'right' }}>
                    {date ? dateUtils.displayFormatDate(date) : '-'}
                  </div>
                );
              }, [])}
            />

            <Column
              style={{ textAlign: 'right', height: '40px', maxWidth: '300px' }}
              field="paymentStatus"
              header="סטטוס תשלום"
              headerStyle={{
                height: '45px',
                maxHeight: '45px',
                padding: '0',
              }}
              showFilterMenu={false}
              filter={true}
              filterElement={statusFilterElement}
              filterMatchMode={FilterMatchMode.EQUALS}
              body={useCallback((rowData: EnrichedUserPaymentService) => {
                const severityMap = {
                  שולם: 'success',
                  'שולם חלקית': 'warning',
                  'לא שולם': 'danger',
                } as const;

                return (
                  <Tag
                    style={{ width: '70px', textAlign: 'center' }}
                    severity={severityMap[rowData.paymentStatus]}
                    value={rowData.paymentStatus}
                  />
                );
              }, [])}
            />

            <Column
              style={{ textAlign: 'center', height: '40px', width: '6rem' }}
              field="סטטוס"
              headerStyle={{
                height: '45px',
                maxHeight: '45px',
                padding: '0',
                paddingRight: '16.5px',
              }}
              body={useCallback(
                (rowData: UserPaymentServiceWithPriceList) => {
                  const currentPriceToPay = rowData.currentPrices.price;
                  const totalPaymentAmount =
                    rowData.userPayments?.reduce(
                      (sum, payment) => sum + payment.itemAmount,
                      0,
                    ) ?? 0;
                  const remainingAmount =
                    currentPriceToPay - totalPaymentAmount;

                  const isFromProgram =
                    !!rowData.allocations?.allocationEntity?.programId;
                  const hasAnyPayment = (rowData.userPayments?.length ?? 0) > 0;
                  const canDelete = !isFromProgram && !hasAnyPayment;

                  return (
                    <div
                      style={{
                        display: 'flex',
                        gap: '8px',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <Button
                        style={{
                          height: '25px',
                          fontSize: '14px',
                          textAlign: 'center',
                          visibility:
                            remainingAmount <= 0 ? 'hidden' : 'visible',
                          padding: '0px',
                          paddingRight: '8px',
                          paddingLeft: '8px',
                        }}
                        label="לתשלום"
                        outlined={true}
                        raised={true}
                        onClick={(e) => {
                          e.stopPropagation();
                          onPayment(
                            rowData.allocations.allocationEntity.id,
                            rowData.paymentService.paymentServiceName,
                            remainingAmount,
                          );
                        }}
                      />

                      <Button
                        icon="pi pi-trash"
                        severity="danger"
                        text
                        /*disabled={!canDelete || deleteLoading}*/
                        disabled={false}
                        loading={
                          deleteLoading &&
                          allocationIdToDelete ===
                            rowData.allocations.allocationEntity.id
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          setAllocationIdToDelete(
                            rowData.allocations.allocationEntity.id,
                          );
                          setShowDeleteAllocationDialog(true);
                        }}
                      />
                    </div>
                  );
                },
                [onPayment, deleteLoading, allocationIdToDelete],
              )}
            />
          </DataTable>

          <style>{`
        .payment-summary {
          box-shadow: 0 2px 4px rgba(0,0,0,0.05);
          border: 1px solid #e0e0e0;
        }
        
        .p-datatable .p-datatable-tbody > tr {
          cursor: pointer;
          transition: background-color 0.15s ease-in-out;
        }
        
        .p-datatable .p-datatable-tbody > tr:not(.p-highlight):hover {
          background-color: #f0f7ff !important;
        }
        
        .p-datatable .p-datatable-tbody > tr.p-row-expanded {
          background-color: #f8f9fa;
        }
        
        .p-datatable .p-datatable-tbody > tr.p-row-expanded > td {
          border-bottom: none;
        }
        
        .expanded-payments-table .p-datatable-wrapper {
          border-radius: 8px;
          overflow: hidden;
        }
        
        .p-row-expanded > td {
          background-color: #f8f9fa !important;
        }
        
        .expander-icon {
          pointer-events: none;
        }
      `}</style>
        </>
      );
    },
  );
